import { requiredStringSchema } from "@gigpro/ui/Form/utils";
import { fileAttachmentSchema, tipPolicySchema } from "api/resources/shared";
import * as z from "zod/mini";
import { makeApiResource } from "./resource";
import { requirementSchema } from "./skills";
import { templateUniformSelectionSchema, uniformSelectionSchema } from "./uniforms";

export const gigTemplateSchema = makeApiResource({
  name: z.string(),
  skill_id: z.number(),
  skill_name: z.optional(z.string()),
  location_id: z.number(),
  no_experience_required: z.boolean(),
  hourly_rate: z.coerce.number(),
  position_description: requiredStringSchema,
  private_notes: z.nullish(z.string()),
  requirements: z.array(z.omit(requirementSchema, { skill: true })),
  auto_select_enabled: z.nullish(z.boolean()),
  attachments: z.nullish(z.array(fileAttachmentSchema)),
  // "Does this shift include tips?" answer persisted on the template (WORK-8452). Never required here;
  // the per-skill requirement is enforced on the position form and by gig create.
  tip_policy: z.nullish(tipPolicySchema),
  // Uniform selection persisted on the template (CORE-6358, backend CORE-6242). Read shape mirrors the
  // Request-a-Pro block so it feeds straight back into the gig-create submit; the backend returns null
  // when the template has no uniform, which we normalize to `undefined` (an absent selection). The
  // trailing `.optional()` keeps `uniform` an optional key (the transform alone would make it required).
  //
  // The backend may persist a selection type this UI can't render into the picker; `.catch(undefined)`
  // falls back to "no stored selection" (pre-fill then uses the position default) instead of letting an
  // unsupported shape fail the whole template-list parse — which would drop every template (and crash
  // the picker). Kept as forward-compat resilience for any future selection type.
  uniform: z.optional(
    z.pipe(
      z.catch(z.nullish(uniformSelectionSchema), undefined),
      z.transform((selection) => selection ?? undefined),
    ),
  ),
});
export type GigTemplate = z.infer<typeof gigTemplateSchema>;

export const gigTemplatePayloadSchema = z.extend(
  z.omit(gigTemplateSchema, { skill_name: true, hourly_rate: true, attachments: true }),
  {
    hourly_rate: z.coerce.number().check(z.gte(15), z.lte(75)),
    attachment_ids: z.optional(z.array(z.string())),
    // Write block persists item_ids only (no free-text custom_items). Omitted = unchanged; a block =
    // upsert. Passing the gig-create form block through this schema strips its `custom_items`.
    uniform: z.nullish(templateUniformSelectionSchema),
  },
);

export type GigTemplatePayload = z.infer<typeof gigTemplatePayloadSchema>;
